import { useEffect, useState } from 'react';

interface UseBackButtonOptions {
  preventBack?: boolean;
  onBackPress?: () => void;
  allowBackNavigation?: boolean;
  exitMessage?: {
    pwa: string;
    browser: string;
  };
}

export const useBackButton = (options: UseBackButtonOptions = {}) => {
  const {
    preventBack = true,
    onBackPress,
    allowBackNavigation = false,
    exitMessage = {
      pwa: "Press back again to close the app",
      browser: "Use navigation menu to go back"
    }
  } = options;

  const [showExitMessage, setShowExitMessage] = useState(false);

  // Function to reset the back button state (useful when navigation happens)
  const resetBackButtonState = () => {
    setShowExitMessage(false);
  };

  useEffect(() => {
    if (!preventBack) return;

    let backPressedOnce = false;
    let backPressTimer: NodeJS.Timeout;

    const handleBackButton = (event: PopStateEvent) => {
      // If back navigation is allowed, let it proceed normally
      if (allowBackNavigation) {
        if (onBackPress) {
          onBackPress();
        }
        return; // Don't prevent default, allow normal navigation
      }

      event.preventDefault();
      
      // Check if running as PWA (installed web app)
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    (window.navigator as any).standalone || 
                    document.referrer.includes('android-app://');

      // Call custom handler if provided
      if (onBackPress) {
        onBackPress();
        return;
      }

      if (isPWA) {
        // PWA behavior: double tap to close
        if (!backPressedOnce) {
          backPressedOnce = true;
          setShowExitMessage(true);
          
          // Reset after 2 seconds
          backPressTimer = setTimeout(() => {
            backPressedOnce = false;
            setShowExitMessage(false);
          }, 2000);
        } else {
          // Second press - close the app
          clearTimeout(backPressTimer);
          backPressedOnce = false;
          setShowExitMessage(false);
          
          if ('exitApp' in window) {
            (window as any).exitApp();
          } else {
            // Fallback for browsers that support it
            window.close();
          }
        }
      } else {
        // Browser behavior: prevent going back, show message briefly
        setShowExitMessage(true);
        setTimeout(() => setShowExitMessage(false), 2000);
      }
      
      // Push current state back to prevent actual navigation
      window.history.pushState(null, '', window.location.pathname);
    };

    // Add initial history state
    window.history.pushState(null, '', window.location.pathname);
    
    // Listen for back button
    window.addEventListener('popstate', handleBackButton);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handleBackButton);
      if (backPressTimer) {
        clearTimeout(backPressTimer);
      }
    };
  }, [preventBack, onBackPress, allowBackNavigation]);

  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
               (window.navigator as any).standalone || 
               document.referrer.includes('android-app://');

  const currentMessage = isPWA ? exitMessage.pwa : exitMessage.browser;

  return {
    showExitMessage,
    currentMessage,
    isPWA,
    resetBackButtonState
  };
};
